import React from "react";
import PlantValue from "../../components/ui/PlantValue";
import { getPlantCompactLabel } from "../../services/plantDisplay";

export const buildNameCodeIdLabel = ({ name, code, id, fallbackId }) => {
  const resolvedId = id || fallbackId || "";
  const resolvedName = name || resolvedId || "-";
  const seen = new Set([resolvedName]);
  const suffix = [code, resolvedId]
    .filter((value) => value && !seen.has(value))
    .map((value) => {
      seen.add(value);
      return `(${value})`;
    })
    .join(" ");

  return suffix ? `${resolvedName} ${suffix}` : resolvedName;
};

export const getPlantDetails = (metaData, plantId) => {
  if (!plantId) return null;
  const key = String(plantId);
  return metaData?.plant_details?.[key] || metaData?.plant_details?.[plantId] || null;
};

export const getPlantLabel = (row, metaData) => {
  const plant = getPlantDetails(metaData, row?.plant_id);
  return getPlantCompactLabel(plant, row?.plant_id);
};

export const renderPlantCell = (row, metaData) => {
  const plant = getPlantDetails(metaData, row?.plant_id);
  return <PlantValue plant={plant} fallbackPlantId={row?.plant_id} />;
};

export const getItemDetails = (metaData, plantId, itemId) => {
  if (!plantId || !itemId) return null;
  return metaData?.item_details?.[`${plantId}:${itemId}`] || null;
};

export const getItemLabel = (row, metaData) => {
  const item = getItemDetails(metaData, row?.plant_id, row?.item_id);

  return buildNameCodeIdLabel({
    name: item?.item_name,
    code: item?.item_code,
    id: item?.item_id,
    fallbackId: row?.item_id,
  });
};

export const getLineDetails = (metaData, plantId, lineId) => {
  if (!plantId || !lineId) return null;
  return metaData?.line_details?.[`${plantId}:${lineId}`] || null;
};

export const getLineLabel = (row, metaData) => {
  const line = getLineDetails(metaData, row?.plant_id, row?.line_id);

  return buildNameCodeIdLabel({
    name: line?.line_name,
    code: line?.line_code,
    id: line?.line_id,
    fallbackId: row?.line_id,
  });
};

export const getOemDetails = (metaData, plantId, oemId) => {
  if (!plantId || !oemId) return null;
  return metaData?.oem_details?.[`${plantId}:${oemId}`] || null;
};

export const getOemLabel = (row, metaData) => {
  const oem = getOemDetails(metaData, row?.plant_id, row?.dispatch_to);

  return buildNameCodeIdLabel({
    name: oem?.oem_name,
    code: oem?.oem_code,
    id: oem?.oem_id,
    fallbackId: row?.dispatch_to,
  });
};

export const getProcessDetails = (metaData, plantId, processId) => {
  if (!plantId || !processId) return null;
  return metaData?.process_details?.[`${plantId}:${processId}`] || null;
};

export const getProcessLabel = (row, metaData) => {
  const process = getProcessDetails(metaData, row?.plant_id, row?.process_id);

  return buildNameCodeIdLabel({
    name: process?.process_name,
    code: process?.process_code,
    id: process?.process_id,
    fallbackId: row?.process_id,
  });
};

export const getOperationDetails = (metaData, plantId, operationId) => {
  if (!plantId || !operationId) return null;
  return metaData?.operation_details?.[`${plantId}:${operationId}`] || null;
};

export const getOperationLabel = (row, metaData) => {
  const operation = getOperationDetails(metaData, row?.plant_id, row?.operation_id);

  return buildNameCodeIdLabel({
    name: operation?.operation_name,
    code: operation?.operation_code,
    id: operation?.operation_id,
    fallbackId: row?.operation_id,
  });
};
